import * as z from "zod";

/** The ten posting kinds; see the engine's `Posting` union for what each means. */
export const POSTING_KINDS = [
  "settle",
  "nowhere",
  "contribute",
  "claim",
  "fund",
  "credit",
  "forfeit",
  "payout",
  "charge",
  "surplus",
] as const;

const PostingRecordSchema_ = z.object({
  kind: z.enum(POSTING_KINDS),
  from: z
    .string()
    .optional()
    .describe(
      "The account the value leaves: a returned unit, an exchange item, a pool, or an adjustment. Absent on a charge, which comes from the shopper.",
    ),
  to: z
    .string()
    .optional()
    .describe(
      "The account the value reaches. Absent on a payout, which goes to the shopper, and on a nowhere, which goes to no one.",
    ),
  amount: z
    .number()
    .optional()
    .describe(
      "Presentment-currency amount moved, always non-negative. Absent on a settle and a nowhere, which move no money.",
    ),
});

/** The allocation engine's result, serialized: every posting and every account it worked from. */
const AllocationRecordSchema_ = z.object({
  accountingVersion: z
    .string()
    .describe("The rule set the engine ran under; see AccountingVersion."),
  postings: z
    .array(PostingRecordSchema_)
    .describe(
      "Every movement of value, in the order the engine posted them. Sums over these reproduce the netting figures.",
    ),
  accounts: z.object({
    returnedUnits: z.array(
      z.object({
        key: z
          .string()
          .describe(
            "`returned:<returned product id>`, one per unit after quantity and bundle children are split.",
          ),
        productId: z.string(),
        variantId: z.string(),
        payout: z
          .string()
          .describe(
            "Where the unit's worth goes if nothing settles it: `refund`, `storeCredit`, or `none` for a rejected or kept unit.",
          ),
        value: z
          .number()
          .describe(
            "The unit's worth before adjustments, in presentment currency: what it contributes to its pool, or what a settled exchange item is valued at. Can be below zero when a discount split across a line leaves it so.",
          ),
      }),
    ),
    exchangeItems: z.array(
      z.object({
        key: z
          .string()
          .describe(
            "`variantPicker:<returned product id>` for a picker replacement, `advanced:<item id>` for a shop-now line, or `advanced:<item id>:<n>` for one unit of that line once a version splits it.",
          ),
        productId: z.string(),
        variantId: z.string(),
        value: z
          .number()
          .optional()
          .describe(
            "What the exchange item costs the shopper, in presentment currency: list price less its share of the bucket discount, plus tax, the same measure as a returned unit's value. A settled exchange item keeps it as a fact on the account while no money moves through it. Absent on a picker replacement the document holds no price for.",
          ),
      }),
    ),
    adjustments: z.array(
      z.object({
        key: z
          .string()
          .describe(
            "`<adjustment type>:<returned product id>:<index>` for an item-level adjustment, or `<adjustment type>:<index>` for a return-level one.",
          ),
        amount: z
          .number()
          .describe(
            "Magnitude in presentment currency, always non-negative; whether it is a fee or a bonus is the posting kind that touches it.",
          ),
        adjustmentType: z.string().describe("The AdjustmentType it came from."),
        unit: z
          .string()
          .optional()
          .describe(
            "The returned unit an item-level adjustment sits on; absent for a return-level one.",
          ),
      }),
    ),
    bonuses: z.array(
      z.object({
        key: z.string().describe("`bonus:<name>`."),
        name: z.string(),
        rank: z
          .number()
          .describe(
            "Spend order: a lower rank is drawn on first when a fee outruns the pools.",
          ),
        amount: z
          .number()
          .describe(
            "What the bonus can cover, in presentment currency. What it did not cover is forfeited.",
          ),
      }),
    ),
  }),
});
export const AllocationRecordSchema: z.ZodType<AllocationRecord> =
  AllocationRecordSchema_;
export interface AllocationRecord extends z.infer<
  typeof AllocationRecordSchema_
> {}

/** How many units of each variant a returned unit settled item for item. */
export function settledUnitsByVariantId(
  record: AllocationRecord,
): Map<string, number> {
  const variantByExchangeItem = new Map(
    record.accounts.exchangeItems.map((exchangeItem) => [
      exchangeItem.key,
      exchangeItem.variantId,
    ]),
  );
  const counts = new Map<string, number>();
  for (const posting of record.postings) {
    if (posting.kind !== "settle" || posting.to === undefined) {
      continue;
    }
    const variantId = variantByExchangeItem.get(posting.to);
    if (variantId !== undefined) {
      counts.set(variantId, (counts.get(variantId) ?? 0) + 1);
    }
  }
  return counts;
}
